/**
 * XR Input Context — Pushed onto the input context stack during XR sessions.
 *
 * Maps XR controller buttons and axes to engine action names.
 * Games read input.isPressed('XR_Trigger_Right') etc.
 */

import type { App } from '../core/App.js';
import { ActionType, type InputContext, type InputSystem } from '../input/InputSystem.js';

const XR_CONTEXT_NAME = 'XR_Gameplay';

const xrContext: InputContext = {
  name: XR_CONTEXT_NAME,
  priority: 100, // Above keyboard/mouse (which are typically 0-10)
  consume: true,
  enabled: true,
  bindings: [
    // Right hand
    { action: 'XR_Trigger_Right', type: ActionType.Trigger },
    { action: 'XR_Grip_Right', type: ActionType.Button },
    { action: 'XR_Thumbstick_Right', type: ActionType.Axis2D },
    { action: 'XR_A_Button', type: ActionType.Button },
    { action: 'XR_B_Button', type: ActionType.Button },
    // Left hand
    { action: 'XR_Trigger_Left', type: ActionType.Trigger },
    { action: 'XR_Grip_Left', type: ActionType.Button },
    { action: 'XR_Thumbstick_Left', type: ActionType.Axis2D },
    { action: 'XR_X_Button', type: ActionType.Button },
    { action: 'XR_Y_Button', type: ActionType.Button },
  ],
};

/**
 * Push the XR input context onto the input system's context stack.
 * Called when an XR session starts.
 */
export function createXRInputContext(app: App): void {
  const inputSystem = app.world.getSchedule()
    .find(s => s.name === 'Input');
  if (!inputSystem) return;

  // Access InputSystem from world systems to push context
  // The InputSystem is registered as a system; we need to find it
  const systems = (app.world as any).scheduler?.systems as any[] | undefined;
  if (!systems) return;

  const input = systems.find((s: any) => s.name === 'Input' && typeof s.pushContext === 'function');
  if (input) {
    (input as InputSystem).pushContext(xrContext);
  }
}

/**
 * Remove the XR input context from the input system's context stack.
 * Called when an XR session ends.
 */
export function removeXRInputContext(app: App): void {
  const systems = (app.world as any).scheduler?.systems as any[] | undefined;
  if (!systems) return;

  const input = systems.find((s: any) => s.name === 'Input' && typeof s.removeContext === 'function');
  if (input) {
    (input as InputSystem).removeContext(XR_CONTEXT_NAME);
  }
}
